import React from 'react';

import type { MayBe } from 'types/common';

import CachedMedia from 'utils/CachedMedia';

/**
 * Browsers share the animation state between images with the same URL: a GIF that plays once
 * stays frozen on its last frame when displayed again. A fresh object URL on each display
 * restarts the animation from the first frame. Falls back to `src` if the GIF cannot be loaded.
 */
const useReplayableGif = (src: string, isDisplayed: boolean) => {
  const [objectUrl, setObjectUrl] = React.useState<MayBe<string>>(undefined);

  React.useEffect(() => {
    if (!isDisplayed) {
      return undefined;
    }

    let createdUrl: MayBe<string>;
    let isCancelled = false;

    const createObjectUrl = async () => {
      try {
        const blob = await new CachedMedia({ path: src }).getAsBlob();

        if (!isCancelled) {
          createdUrl = URL.createObjectURL(blob);
          setObjectUrl(createdUrl);
        }
      } catch {
        // Keep the static GIF
      }
    };

    createObjectUrl();

    return () => {
      isCancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
      setObjectUrl(undefined);
    };
  }, [src, isDisplayed]);

  return objectUrl ?? src;
};

export default useReplayableGif;
